import { isNumber } from "@foretagsplatsen/core-helpers/number";
import { Widget } from "@foretagsplatsen/widgetjs";

/**
 * Widget for the budget editor fields.
 * Responsible for displaying values, delegating input events and listening
 * on update events.
 *
 * @param {BudgetReport} spec.budgetReport - The report to which the field belongs
 * @param {abstractRow} spec.row - The row in the report to which the field
 * belongs to
 * @param {abstractColumn} spec.column - The column in the report to which the
 * field belongs to
 * @param {event} spec.onBudgetFieldChanged - Event to be triggered for input
 * change event
 * @param {event} spec.onBudgetFieldKeyup - Event to be triggered for input
 * keyup event
 */
export default class BudgetEditorFieldWidget extends Widget {
	constructor({
		budgetReport,
		row,
		column,
		onBudgetFieldChanged,
		onBudgetFieldKeyup,
	}) {
		super(...arguments);

		this._budgetReport = budgetReport;
		this._row = row;
		this._column = column;
		this._onBudgetFieldChanged = onBudgetFieldChanged;
		this._onBudgetFieldKeyup = onBudgetFieldKeyup;
	}

	getColumn() {
		return this._column;
	}

	select() {
		this._getInputElement().focus();
		this._getInputElement().select();
	}

	/**
	 * Write "-" inplace of the existing value if the receiver isn't
	 * an input field.
	 */
	setTemporaryValue() {
		if (this._isInputField()) {
			return;
		}

		this._writeValue("-");
	}

	/**
	 * Update the widget with the corresponding cell data in the
	 * report. If `force` is `false`, only update the receiver if it
	 * isn't an input field.
	 */
	updateValue({ force = false } = {}) {
		if (!force && this._isInputField()) {
			return;
		}

		this._writeValue(this._computeValue());
	}

	renderContentOn(html) {
		const value = this._computeValue();

		if (!this._isInputField()) {
			html.render(value);
			return;
		}

		html.input({
			value,
			id: this.constructor.generateInputId({
				row: this._row,
				column: this._column,
			}),
			type: "text",
			click: () => {
				this._getInputElement().select();
			},
			change: (event) => this._inputFieldChanged(event),
			keyup: (event) => {
				this._onBudgetFieldKeyup({
					event,
					column: this._column,
					row: this._row,
					budgetField: this,
				});
			},
		});
	}

	_inputFieldChanged(event) {
		const value = Number.parseFloat(event.target.value.replaceAll(" ", ""));

		if (!isNumber(value)) {
			this._markInvalid();
			return;
		}

		this._getInputElement().removeClass("has-error");

		this._onBudgetFieldChanged({
			value,
			column: this._column,
			row: this._row,
		});

		const formattedValue = this._budgetReport.getFormattedData({
			row: this._row,
			column: this._column,
		});

		event.target.value = formattedValue;
	}

	_isInputField() {
		return (
			this._column.isEditableBudgetColumn() && this._row.isAccountRow()
		);
	}

	_getInputElement() {
		return this.asJQuery().find("input").first();
	}

	_markInvalid() {
		this._getInputElement().addClass("has-error");
	}

	_computeValue() {
		return this._budgetReport.getFormattedData({
			row: this._row,
			column: this._column,
		});
	}

	_writeValue(value) {
		if (this._isInputField()) {
			this._writeValueInInput(value);
		} else {
			this._writeValueAsText(value);
		}
	}

	_writeValueAsText(value) {
		this.asJQuery().text(value);
	}

	_writeValueInInput(value) {
		const input = this._getInputElement();

		if (input.length) {
			input[0].value = value;
		}
	}

	static generateInputId({ row, column }) {
		return `row${row.getId()}-column${column.getId()}`;
	}
}
